import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthSignalService } from '../state/auth.signal';
import { AuthService } from './auth.service';
import { catchError, map, of, retry, throwError, timer } from 'rxjs';

/**
 * ¿El fallo dice "no tienes sesión" o "el servidor tuvo un problema"?
 *
 * Sin conexión (status 0) o un 5xx NO significan que la sesión haya caducado: la
 * cookie sigue siendo válida. Un 401 real lo resuelve antes el jwtInterceptor
 * renovando el token, así que si llega aquí es que la sesión sí terminó.
 */
function esFalloTransitorio(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
}

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private router = inject(Router);
  private authSignal = inject(AuthSignalService);
  private authService = inject(AuthService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // If the signal is already populated (e.g. navigating within the app), allow immediately
    if (this.authSignal.isAuthenticated()) {
      return true;
    }

    // Otherwise, try to restore session from cookie (page refresh scenario)
    return this.authService.loadCurrentUser().pipe(
      // Un parpadeo del backend (Redis caído, reinicio, red) hacía que el usuario
      // apareciera "deslogueado" en esta pestaña aunque su sesión estuviera intacta
      // en las demás. Se reintenta antes de darla por perdida.
      retry({
        count: 2,
        delay: (error, intento) => (esFalloTransitorio(error) ? timer(intento * 700) : throwError(() => error)),
      }),
      map(() => true),
      catchError(() => {
        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return of(false);
      })
    );
  }
}
